import { auth, db } from "@/core/config/firebase";
import {
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  EmailAuthProvider,
  linkWithCredential,
  type User,
  type UserCredential,
} from "firebase/auth";
import {
  doc,
  getDoc,
  updateDoc,
  onSnapshot,
  type DocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import { authStore } from "@/core/store/auth.store";
import { configuracionStore } from "@/core/store/configuracion.store";
import { ventasStore } from "@/core/store/ventas.store";
import { useClientesStore } from "@/core/store/clientes.store";
import { COLECCIONES } from "@/core/constants/collections";
import { ADMIN_EMAILS } from "@/core/constants/defaults";
import type { UserProfile } from "@/core/types";

// Se mantiene la variable para la desuscripción del listener de Firestore.
let unsubscribeUserProfileListener: (() => void) | null = null;

/**
 * Inicializa el listener principal de autenticación de la aplicación.
 */
export function initializeAuthListener(): void {
  onAuthStateChanged(auth, async (firebaseUser: User | null) => {
    // Si existe un listener anterior, se desuscribe para evitar fugas de memoria.
    if (unsubscribeUserProfileListener) {
      unsubscribeUserProfileListener();
      unsubscribeUserProfileListener = null;
    }

    if (firebaseUser) {
      try {
        console.log(
          `[AuthService] Usuario ${firebaseUser.uid} autenticado. Buscando perfil...`,
        );

        const userProfileRef = doc(db, COLECCIONES.USUARIOS, firebaseUser.uid);
        const docSnap = await getDoc(userProfileRef);

        if (docSnap.exists()) {
          const profileData: DocumentData = docSnap.data();
          let userRol = profileData.rol;

          // Auto-upgrade a admin si el email coincide y aún no es admin
          if (profileData.email && ADMIN_EMAILS.includes(profileData.email) && userRol !== 'admin') {
              console.log(`[AuthService] Actualizando rol a admin para ${profileData.email}`);
              await updateDoc(userProfileRef, { rol: 'admin' });
              userRol = 'admin';
          }

          // Usar la foto de Firebase Auth si está disponible (más actualizada, especialmente cuando se vincula con Google)
          let photoURL = firebaseUser.photoURL || profileData.photoURL || "";

          // Si no hay foto pero el usuario tiene Google vinculado, buscar en los providers
          if (!photoURL && firebaseUser.providerData) {
            const googleProvider = firebaseUser.providerData.find(
              (provider) => provider.providerId === "google.com",
            );
            if (googleProvider?.photoURL) {
              photoURL = googleProvider.photoURL;
            }
          }

          const userProfile: UserProfile = {
            uid: docSnap.id,
            nombre: profileData.nombre,
            apellido: profileData.apellido,
            email: profileData.email,
            photoURL: photoURL,
            rol: userRol
          };

          authStore.setUser(userProfile, firebaseUser);
          console.log(
            "[AuthService] Perfil de usuario inicial cargado en el store.",
          );

          // Inicializar stores de datos críticos que dependen del usuario
          console.log(
            "[AuthService] Inicializando stores de configuración, ventas y clientes...",
          );

          await Promise.all([
            configuracionStore.init(userProfile.uid),
            ventasStore.init(userProfile.uid),
            useClientesStore.init(userProfile.uid)
          ]);
          console.log(
            "[AuthService] Stores inicializados exitosamente.",
          );

          // Se establece un listener en tiempo real para el perfil del usuario.
          unsubscribeUserProfileListener = onSnapshot(
            userProfileRef,
            (updatedDoc: DocumentSnapshot) => {
              if (updatedDoc.exists()) {
                const updatedProfileData: DocumentData = updatedDoc.data();
                
                let userRol = updatedProfileData.rol;
                // Auto-upgrade a admin si el email coincide
                if (updatedProfileData.email && ADMIN_EMAILS.includes(updatedProfileData.email) && userRol !== 'admin') {
                    console.log(`[AuthService] Actualizando rol a admin para ${updatedProfileData.email}`);
                    updateDoc(userProfileRef, { rol: 'admin' });
                    userRol = 'admin';
                }

                // Usar la foto de Firebase Auth si está disponible (más actualizada)
                let photoURL =
                  firebaseUser.photoURL || updatedProfileData.photoURL || "";

                // Si no hay foto pero el usuario tiene Google vinculado, buscar en los providers
                if (!photoURL && firebaseUser.providerData) {
                  const googleProvider = firebaseUser.providerData.find(
                    (provider) => provider.providerId === "google.com",
                  );
                  if (googleProvider?.photoURL) {
                    photoURL = googleProvider.photoURL;
                  }
                }

                const updatedProfile: UserProfile = {
                  uid: updatedDoc.id,
                  nombre: updatedProfileData.nombre,
                  apellido: updatedProfileData.apellido,
                  email: updatedProfileData.email,
                  photoURL: photoURL,
                  rol: userRol,
                };

                // Actualiza el store solo si los datos del perfil han cambiado.
                if (
                  JSON.stringify(authStore.getState().user) !==
                  JSON.stringify(updatedProfile)
                ) {
                  authStore.setUser(updatedProfile, firebaseUser);
                  console.log(
                    "[AuthService] Perfil de usuario actualizado en tiempo real.",
                  );
                }
              } else {
                console.error(
                  `[AuthService] Error Crítico: Se ha borrado el perfil para ${firebaseUser.uid}. Forzando cierre.`,
                );
                void signOut(auth);
              }
            },
            (error: Error) => {
              console.error(
                "[AuthService] Error en el listener de tiempo real:",
                error,
              );
              void signOut(auth);
            },
          );
        } else {
          console.error(
            `[AuthService] Error Crítico: No se encontró perfil para ${firebaseUser.uid}. Forzando cierre.`,
          );
          void signOut(auth);
        }
      } catch (error) {
        console.error(
          "[AuthService] Error al obtener el perfil de usuario:",
          error,
        );
        authStore.clearUser();
        void signOut(auth);
      }
    } else {
      // DESTRUIR STORES AL CERRAR SESIÓN
      console.log("[AuthService] Usuario desautenticado. Limpiando stores...");
      configuracionStore.destroy();
      ventasStore.destroy();
      useClientesStore.destroy();
      authStore.clearUser();
      console.log("[AuthService] Usuario desautenticado. Stores limpiados.");
    }

    // Señal para el router, indicando que el estado de autenticación está listo.
    window.dispatchEvent(new CustomEvent("auth-state-ready"));
  });
}

/**
 * Inicia sesión de un usuario con correo y contraseña.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<UserCredential>}
 */
export const login = (
  email: string,
  password: string,
): Promise<UserCredential> => {
  return signInWithEmailAndPassword(auth, email, password);
};

/**
 * Registra un nuevo usuario con correo y contraseña.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<UserCredential>}
 */
export const register = (
  email: string,
  password: string,
): Promise<UserCredential> => {
  return createUserWithEmailAndPassword(auth, email, password);
};

/**
 * Inicia sesión con Google.
 * Permite al usuario elegir entre sus cuentas de Google existentes.
 * @returns {Promise<UserCredential>}
 */
export const loginWithGoogle = (): Promise<UserCredential> => {
  const provider = new GoogleAuthProvider();
  // Agregar scopes necesarios para obtener información del perfil
  provider.addScope("email");
  provider.addScope("profile");
  // NO usar el parámetro 'hd' para permitir que el usuario elija cualquier cuenta de Google
  return signInWithPopup(auth, provider);
};

/**
 * Vincula una credencial de correo/contraseña a la cuenta actual del usuario.
 * Útil cuando el usuario tiene una cuenta vinculada con Google y quiere agregar también correo/contraseña.
 * @param {string} email - El correo electrónico
 * @param {string} password - La contraseña
 * @returns {Promise<UserCredential>}
 */
export const linkEmailPassword = async (
  email: string,
  password: string,
): Promise<UserCredential> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error(
      "No hay un usuario autenticado. Debes iniciar sesión primero.",
    );
  }

  const credential = EmailAuthProvider.credential(email, password);
  return linkWithCredential(user, credential);
};

/**
 * Cierra la sesión del usuario actual.
 * @returns {Promise<void>}
 */
export const logout = (): Promise<void> => {
  // Se limpia el listener del perfil al cerrar sesión.
  if (unsubscribeUserProfileListener) {
    unsubscribeUserProfileListener();
    unsubscribeUserProfileListener = null;
  }
  // Destruir los stores
  configuracionStore.destroy();
  ventasStore.destroy();
  useClientesStore.destroy();
  return signOut(auth);
};
